import React from 'react'
import { ComponentConfig, Slot } from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  TypographyProps,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyTypography,
  FontWeight,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import * as styles from './PollAnswer.module.scss'
export type { PollDropdownButtonProps as PollDropdownButton } from '../PollDropdownButton/PollDropdownButton'

export type PollAnswerProps = {
  id?: string
  text: string
  answerBgColor: string
  answerTextColor: string
  answerBgHoverColor: string
  /** Content placed before the percentage bar (image, video, etc.). Presence triggers IMAGE layout. */
  beforePercentage: Slot
  /** Content placed after the percentage bar (dropdown button, etc.). */
  afterPercentage: Slot
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
}

export const POLL_ANSWER_DEFAULT_PROPS: PollAnswerProps = {
  text: 'Answer option',
  answerBgColor: '#4C3371',
  answerTextColor: '#000',
  answerBgHoverColor: '#4C3371',
  beforePercentage: [],
  afterPercentage: [],
}

const ANSWER_FIELD_TEXT = {
  type: 'text' as const,
  label: 'Answer Text',
}

const ANSWER_FIELD_BG_COLOR = {
  type: 'custom' as const,
  label: 'Answer Border Color',
  render: (props: any) => <ColorField {...props} />,
}

const ANSWER_FIELD_TEXT_COLOR = {
  type: 'custom' as const,
  label: 'Answer Text Color',
  render: (props: any) => <ColorField {...props} />,
}

const ANSWER_FIELD_BG_HOVER_COLOR = {
  type: 'custom' as const,
  label: 'Answer Border Hover Color',
  render: (props: any) => <ColorField {...props} />,
}

export const PuckPollAnswer: ComponentConfig<PollAnswerProps> = {
  label: 'Poll Answer',
  defaultProps: POLL_ANSWER_DEFAULT_PROPS,
  fields: {
    text: ANSWER_FIELD_TEXT,
    answerBgColor: ANSWER_FIELD_BG_COLOR,
    answerTextColor: ANSWER_FIELD_TEXT_COLOR,
    answerBgHoverColor: ANSWER_FIELD_BG_HOVER_COLOR,
    beforePercentage: {
      type: 'slot' as const,
      disallow: ['PollContainer', 'PollQuestion', 'PollAnswer', 'PollProduct', 'PollProductPanel', 'PollDropdownButton', 'PollNextQuestionButton'],
    },
    afterPercentage: {
      type: 'slot' as const,
      disallow: ['PollContainer', 'PollQuestion', 'PollAnswer', 'PollProduct', 'PollProductPanel', 'PollDropdownButton', 'PollNextQuestionButton'],
    },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    textTransform: textTransformField,
    fontSize: { type: 'text' as const, label: 'Font Size', placeholder: '16px' },
    fontWeight: { type: 'select' as const, label: 'Font Weight', options: fontWeightOptions },
  },
  render: ({
    text,
    answerBgColor,
    answerTextColor,
    answerBgHoverColor,
    beforePercentage: BeforeSlot,
    afterPercentage: AfterSlot,
    fontFamily,
    fontFamilyFallback,
    textTransform,
    fontSize,
    fontWeight,
  }) => (
    <li
      className={`editorial-answer has-text ${styles.pollAnswerContainer}`}
      style={
        {
          '--poll-answer-border-color': answerBgColor,
          '--poll-answer-text-color': answerTextColor,
          '--poll-answer-border-color-hover': answerBgHoverColor,
        } as React.CSSProperties
      }
    >
      <BeforeSlot minEmptyHeight={28} className={styles.pollAnswerBeforeSlot} disallow={['PollContainer', 'PollQuestion', 'PollAnswer', 'PollProduct', 'PollProductPanel', 'PollDropdownButton', 'PollNextQuestionButton']} />
      {text && (
        <span
          style={{
            ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
            ...(fontSize ? { fontSize } : {}),
            ...(fontWeight ? { fontWeight: Number(fontWeight) || undefined } : {}),
          }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      )}
      <AfterSlot minEmptyHeight={28} className={styles.pollAnswerAfterSlot} disallow={['PollContainer', 'PollQuestion', 'PollAnswer', 'PollProduct', 'PollProductPanel', 'PollDropdownButton', 'PollNextQuestionButton']} />
    </li>
  ),
}
